import { rememberPlan } from './billingIntent';
import { Capacitor } from '@capacitor/core';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, CircularProgress, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { FiLock } from 'react-icons/fi';
import { get } from '../http/api';
import copy from './copy.json';

export default function ProfileVisitGate() {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || 'fr').split('-')[0];
  const text = copy[language] || copy.fr;
  const id = new URLSearchParams(location.search).get('profileVisit');
  const searchOffer = new URLSearchParams(location.search).get('premiumFeature') === 'advanced-search';
  const matchingOffer = new URLSearchParams(location.search).get('premiumFeature') === 'matching';
  const matchingNotice = matchingOffer ? new URLSearchParams(location.search).get('matchingNotice') : '';
  const noticeText = ({ requests: text.matchingRequestsLimit, matches: text.matchingMatchesLimit, unavailable: text.matchingUnavailable, targetLimit: text.matchingTargetLimit, setup: text.matchingSetup })[matchingNotice];
  const [status, setStatus] = useState('loading');
  const [plans, setPlans] = useState(false);
  const [interval, setInterval] = useState('yearly');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const openMatching = (event) => {
      const params = new URLSearchParams(location.search);
      params.delete('profileVisit');
      params.set('premiumFeature', 'matching');
      if (event.detail?.notice) params.set('matchingNotice', event.detail.notice);
      else params.delete('matchingNotice');
      navigate({ pathname: location.pathname, search: params.toString() });
    };
    window.addEventListener('carrerup:premium-matching', openMatching);
    return () => window.removeEventListener('carrerup:premium-matching', openMatching);
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (searchOffer || matchingOffer) {
      setStatus('locked'); setPlans(searchOffer); setInterval('yearly');
      return undefined;
    }
    if (!id) return undefined;
    let active = true;
    setStatus('loading'); setPlans(false); setInterval('yearly');
    get(`notifications/profile-visit/${encodeURIComponent(id)}`).then((response) => {
      if (!active) return;
      const notification = response.data;
      if (notification?.premium_required === false && notification.username) {
        navigate(`/user/${encodeURIComponent(notification.username)}`, { replace: true });
      } else if (notification?.premium_required === true) {
        setStatus('locked');
      } else setStatus('error');
    }).catch(() => { if (active) setStatus('error'); });
    return () => { active = false; };
  }, [id, searchOffer, matchingOffer, attempt, navigate]);

  const close = () => {
    const params = new URLSearchParams(location.search);
    params.delete('profileVisit');
    params.delete('premiumFeature');
    params.delete('matchingNotice');
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  return <Dialog open={Boolean(id) || searchOffer || matchingOffer} onClose={close} fullWidth maxWidth="xs"
    aria-labelledby="profile-visit-premium-title" dir={language === 'ar' ? 'rtl' : 'ltr'}
    PaperProps={{ sx: { borderRadius: 4, p: 1, color: '#17344f' } }}>
    <DialogTitle id="profile-visit-premium-title">
      <Box sx={{ color: '#c4480a', mb: 1 }}><FiLock aria-hidden="true" /> Premium</Box>
      {noticeText ? text.matchingNoticeTitle : matchingOffer ? text.matchingTitle : searchOffer ? text.searchTitle : text.title}
    </DialogTitle>
    <DialogContent>
      {status === 'loading' ? <Box role="status"><CircularProgress size={22} /> {text.loading}</Box>
        : status === 'error' ? <Typography role="alert">{text.error}</Typography>
        : <>
          <Typography sx={{ mb: 3, lineHeight: 1.7 }}>{noticeText || (matchingOffer ? text.matchingDescription : searchOffer ? text.searchDescription : text.description)}</Typography>
          {matchingOffer && !noticeText && <Typography sx={{ mb: 2 }}>{text.matchingLimits}</Typography>}
          {plans && !noticeText && <>
            <ToggleButtonGroup exclusive value={interval} onChange={(_, value) => value && setInterval(value)} fullWidth aria-label="Premium">
              <ToggleButton value="yearly">{text.yearly}</ToggleButton>
              <ToggleButton value="monthly">{text.monthly}</ToggleButton>
            </ToggleButtonGroup>
            <Typography sx={{ fontSize: '2rem', fontWeight: 800, mt: 2 }}>{interval === 'yearly' ? text.annualPrice : text.monthlyPrice}</Typography>
            <Typography>{interval === 'yearly' ? text.annualDetail : text.monthlyDetail}</Typography>
            {interval === 'yearly' && <Typography sx={{ color: '#23784f', mt: 1 }}>{text.saving}</Typography>}
            <Typography role="status" sx={{ mt: 3, p: 2, bgcolor: '#f2f5f8', borderRadius: 2 }}>{Capacitor.isNativePlatform() ? text.unavailable : <Button variant="contained" onClick={() => { rememberPlan(interval); navigate("/billing"); }}>{text.plans}</Button>}</Typography>
          </>}
        </>}
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={close}>{text.close}</Button>
      {status === 'error' && <Button onClick={() => setAttempt(value => value + 1)}>{text.retry}</Button>}
      {status === 'locked' && !plans && !noticeText && <Button variant="contained" onClick={() => setPlans(true)} sx={{ bgcolor: '#c4480a', '&:hover': { bgcolor: '#9f3805' } }}>{text.plans}</Button>}
    </DialogActions>
  </Dialog>;
}
